import { useEffect } from 'react';
import { toaster } from '@optiaxiom/react';

import { EmptyPage } from '@frontend/components';
import FullScreenLoader from '@frontend/components/FullScreenLoader';
import { ToolsTable } from '@frontend/components/tools-table';
import {
  useBulkToggleChatEnabled,
  useBulkToggleTools,
  useRestoreToolsInPrimaryAgent,
  useToolEnabledInPrimaryAgent,
  useToolStatus,
} from '@frontend/features/tools-management/hooks';
import { GetToolsResponse, Tool } from '@frontend/types';

type ToolsTabProps = {
  filterText: string;
  toolsResponse: GetToolsResponse | undefined;
  isPending: boolean;
  error: Error | null;
  activeServiceProvider: string;
  selectedStatus: string;
  selectedEnabledInPrimaryAgent: string;
};

export function ToolsTab({
  filterText,
  toolsResponse,
  isPending,
  error,
  activeServiceProvider,
  selectedStatus,
  selectedEnabledInPrimaryAgent,
}: ToolsTabProps) {
  const { toggleStatus, isUpdating, updatingToolId } = useToolStatus();
  const {
    toggleEnabledInPrimaryAgent,
    isUpdating: isUpdatingEnabledInPrimaryAgent,
    updatingToolId: updatingToolIdEnabledInPrimaryAgent,
  } = useToolEnabledInPrimaryAgent();
  const { restoreToolsInPrimaryAgent, isRestoring } = useRestoreToolsInPrimaryAgent();
  const { bulkToggle, isToggling: isBulkToggling } = useBulkToggleTools();
  const { bulkToggleChatEnabled, isToggling: isBulkTogglingChat } = useBulkToggleChatEnabled();

  useEffect(() => {
    if (error) {
      toaster.create(error.message || 'Failed to load tools', { type: 'danger' });
    }
  }, [error]);

  const handleToggleActive = (toolId: string, toolName: string, isDisabled: boolean) => {
    toggleStatus({ toolId, toolName, isDisabled });
  };

  const handleToggleEnabledInPrimaryAgent = (
    toolName: string,
    isEnabledInPrimaryAgent: boolean
  ) => {
    // Check if this tool is always-on
    const tool = toolsResponse?.functions.find(t => t.name === toolName);
    if (tool?.always_on_in_primary_agent) {
      toaster.create(
        "This tool is always enabled and cannot be disabled as it's required for the primary agent to function correctly.",
        { type: 'warning' }
      );
      return;
    }

    // If trying to enable (currently disabled)
    if (!isEnabledInPrimaryAgent) {
      const currentEnabledCount =
        toolsResponse?.functions.filter(tool => tool.is_enabled_in_primary_agent).length || 0;

      if (currentEnabledCount >= 128) {
        toaster.create(
          "Tool Limit Exceeded: You've reached the maximum of 128 tools. To enable this tool, please disable another one first.",
          { type: 'danger' }
        );
        return;
      }
    }

    toggleEnabledInPrimaryAgent({ toolName, isEnabledInPrimaryAgent });
  };

  const handleBulkToggle = (
    _serviceId: string,
    functionIds: string[],
    action: 'enable' | 'disable'
  ) => {
    // Filter out system functions before bulk toggling
    const nonSystemFunctionIds = functionIds.filter(functionId => {
      const tool = toolsResponse?.functions.find(t => t.function_id === functionId);
      return tool && !tool.is_system_function;
    });

    if (nonSystemFunctionIds.length === 0) {
      toaster.create('No tools to toggle. System tools cannot be disabled.', { type: 'warning' });
      return;
    }

    bulkToggle({ functionIds: nonSystemFunctionIds, action });
  };

  const handleBulkToggleChatEnabled = (toolNames: string[], action: 'enable' | 'disable') => {
    // Filter out always-on tools when disabling
    const filterableToolNames = toolNames.filter(toolName => {
      const tool = toolsResponse?.functions.find(t => t.name === toolName);
      if (action === 'disable' && tool?.always_on_in_primary_agent) {
        return false;
      }
      return true;
    });

    if (filterableToolNames.length === 0 && action === 'disable') {
      toaster.create(
        'No tools to disable. The selected tools are always enabled and required for the primary agent to function correctly.',
        { type: 'warning' }
      );
      return;
    }

    if (filterableToolNames.length < toolNames.length && action === 'disable') {
      toaster.create(
        'Some tools were skipped as they are always enabled and required for the primary agent to function correctly.',
        { type: 'warning' }
      );
    }

    if (action === 'enable') {
      const currentEnabledCount =
        toolsResponse?.functions.filter(tool => tool.is_enabled_in_primary_agent).length || 0;
      const totalAfterEnable = currentEnabledCount + filterableToolNames.length;

      if (totalAfterEnable > 128) {
        toaster.create(
          `Tool Limit Exceeded: You've reached the maximum of 128 tools. To enable this tool, please disable another one first.`,
          { type: 'danger' }
        );
        return;
      }
    }
    bulkToggleChatEnabled({ toolNames: filterableToolNames, action });
  };

  if (isPending || isRestoring) {
    return <FullScreenLoader />;
  }

  if (!toolsResponse || toolsResponse.functions.length === 0) {
    return <EmptyPage title="No tools found." description="Try adding new tool registry." />;
  }

  const filteredTools = toolsResponse.functions.filter((tool: Tool) => {
    // Filter out forbidden tools - they should not be shown in the UI
    if (tool.forbidden_in_primary_agent) {
      return false;
    }

    // Text search filter
    if (filterText) {
      const lowerCaseFilter = filterText.toLowerCase();
      const matchesText =
        tool.name.toLowerCase().includes(lowerCaseFilter) ||
        (tool.description && tool.description.toLowerCase().includes(lowerCaseFilter));
      if (!matchesText) return false;
    }

    // Status filter
    if (selectedStatus !== 'All') {
      const matchesStatus = tool.is_disabled === (selectedStatus === 'Disabled');
      if (!matchesStatus) return false;
    }

    // Enabled in primary agent filter
    if (selectedEnabledInPrimaryAgent !== 'All') {
      const matchesEnabled =
        tool.is_enabled_in_primary_agent === (selectedEnabledInPrimaryAgent === 'Enabled');
      if (!matchesEnabled) return false;
    }

    // Service provider filter
    if (activeServiceProvider !== 'All') {
      const matchesProvider = tool.service.name === activeServiceProvider;
      if (!matchesProvider) return false;
    }

    return true;
  });

  if (filteredTools.length === 0) {
    return <EmptyPage title="No tools match your search." description="Try a different search." />;
  }

  return (
    <>
      {!isPending && !error && !isRestoring && (
        <ToolsTable
          tools={filteredTools}
          isUpdating={isUpdating}
          updatingToolId={updatingToolId}
          isUpdatingEnabledInPrimaryAgent={isUpdatingEnabledInPrimaryAgent}
          updatingToolIdEnabledInPrimaryAgent={updatingToolIdEnabledInPrimaryAgent}
          onToggleActive={handleToggleActive}
          onToggleEnabledInPrimaryAgent={handleToggleEnabledInPrimaryAgent}
          onBulkToggle={handleBulkToggle}
          isBulkToggling={isBulkToggling}
          onBulkToggleChatEnabled={handleBulkToggleChatEnabled}
          isBulkTogglingChat={isBulkTogglingChat}
          onRestoreToolsClick={
            isRestoring ||
            isUpdatingEnabledInPrimaryAgent ||
            isUpdating ||
            !toolsResponse.is_allow_list_available
              ? undefined
              : restoreToolsInPrimaryAgent
          }
        />
      )}
    </>
  );
}
